import React from 'react';
import {
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
  OutlinedInput,
  Chip,
  Box,
} from '@mui/material';
import { UserRoles } from '../../shared/constants';
import { DOCUMENT_PERMISSION_ROLE_OPTIONS } from '../../shared/documentConstants';

interface PermissionDropdownProps {
  selectedPermissions: UserRoles[];
  onChange: (permissions: UserRoles[]) => void;
}

const SELECT_ALL_VALUE = '__select_all__';

const ALLOWED_PERMISSION_VALUES = new Set(
  DOCUMENT_PERMISSION_ROLE_OPTIONS.map((role) => role.value),
);

const allRoleValues = DOCUMENT_PERMISSION_ROLE_OPTIONS.map((role) => role.value);

const isAllowedPermission = (value: string): value is UserRoles =>
  ALLOWED_PERMISSION_VALUES.has(value as UserRoles);

const sanitizePermissions = (values: string[]): UserRoles[] =>
  values.filter(isAllowedPermission);

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight:
        ITEM_HEIGHT * DOCUMENT_PERMISSION_ROLE_OPTIONS.length +
        ITEM_PADDING_TOP +
        ITEM_HEIGHT,
      width: 286,
    },
  },
};

const PermissionDropdown: React.FC<PermissionDropdownProps> = ({
  selectedPermissions,
  onChange,
}) => {
  const validPermissions = sanitizePermissions(selectedPermissions);

  const isAllSelected =
    allRoleValues.length > 0 &&
    allRoleValues.every((role) => validPermissions.includes(role));

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    const nextValues = typeof value === 'string' ? value.split(',') : value;

    // "Select all" is a control action, never a stored permission.
    if (nextValues.includes(SELECT_ALL_VALUE)) {
      onChange(isAllSelected ? [] : allRoleValues);
      return;
    }

    onChange(sanitizePermissions(nextValues));
  };

  return (
    <Select
      multiple
      displayEmpty
      value={validPermissions}
      onChange={handleChange}
      input={<OutlinedInput size="small" />}
      renderValue={(selected) => {
        if (selected.length === 0) {
          return <em>Access permissions</em>;
        }

        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Chip
              label={`Access permissions: ${selected.length}`}
              size="small"
              onDelete={(e) => {
                e.preventDefault();
                onChange([]);
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
            />
          </Box>
        );
      }}
      MenuProps={MenuProps}
      sx={{ minWidth: 280, backgroundColor: 'white', borderRadius: '4px' }}
    >
      <MenuItem value={SELECT_ALL_VALUE}>
        <Checkbox checked={isAllSelected} />
        <ListItemText primary="Select all" />
      </MenuItem>
      {DOCUMENT_PERMISSION_ROLE_OPTIONS.map((role) => (
        <MenuItem key={role.value} value={role.value}>
          <Checkbox checked={validPermissions.includes(role.value)} />
          <ListItemText primary={role.label} />
        </MenuItem>
      ))}
    </Select>
  );
};

export default PermissionDropdown;
